import { Menu, PanelLeft, Search } from 'lucide-react';
import { Link } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import { BRAND_NAME } from '@/config/nav';
import { NotificationBell } from '@/features/notifications';
import { PresenceSync } from '@/features/presence';
import { TimerIndicator } from '@/features/projects';
import { useCommandPalette } from '@/stores/commandPalette';
import { useSession } from '@/stores/session';
import { Dev13Logo } from './Dev13Logo';
import { useNavCollapse } from './navCollapse';
import { OrgSwitcher } from './OrgSwitcher';
import { ThemeToggle } from './ThemeToggle';
import { UserMenu } from './UserMenu';

interface HeaderProps {
  /** Opens the mobile navigation drawer. */
  onMenuClick: () => void;
}

export function Header({ onMenuClick }: HeaderProps) {
  const openSearch = useCommandPalette((s) => s.setOpen);
  const activeSlug = useSession((s) => s.activeOrgSlug);
  const orgs = useSession((s) => s.organizations);
  const active = orgs.find((o) => o.slug === activeSlug);
  // Subcontracted guests: white-label the shell — show the neutral platform
  // brand (never the agency's org logo/name) and hide the org switcher.
  const whiteLabel = useSession((s) => s.whiteLabel);
  const isGuest = useSession((s) => s.role) === 'guest';
  const brandName = whiteLabel ? BRAND_NAME : (active?.name ?? BRAND_NAME);
  const orgLogo = whiteLabel ? null : active?.logo;
  const sidebarCollapsed = useNavCollapse((s) => s.sidebarCollapsed);
  const toggleSidebar = useNavCollapse((s) => s.toggleSidebar);

  // Active-org branding: prefer logo + name when present, fall back to the
  // default Flexipy mark. Logos come from the backend's storage path; if it
  // looks relative, the api-client base URL handles serving in dev/prod.
  return (
    <header className="bg-sidebar text-sidebar-foreground border-sidebar-border sticky top-0 z-30 flex h-14 items-center gap-2 border-b px-3 md:px-4">
      <Button
        variant="ghost"
        size="icon"
        className="lg:hidden"
        onClick={onMenuClick}
        aria-label="Open navigation menu"
      >
        <Menu />
      </Button>
      {/* Desktop sidebar collapse (Plane-style panel toggle). */}
      <Button
        variant="ghost"
        size="icon"
        className="hidden lg:inline-flex"
        onClick={toggleSidebar}
        aria-pressed={sidebarCollapsed}
        aria-label={sidebarCollapsed ? 'Show sidebar' : 'Hide sidebar'}
        title={sidebarCollapsed ? 'Show sidebar' : 'Hide sidebar'}
      >
        <PanelLeft />
      </Button>

      <Link to="/" className="flex items-center gap-2 font-semibold">
        {whiteLabel ? (
          // Subcontracted guests see the neutral Dev13Systems platform brand.
          <Dev13Logo />
        ) : (
          <>
            {orgLogo ? (
              <img
                src={orgLogo}
                alt={`${brandName} logo`}
                className="size-7 rounded-md object-cover"
              />
            ) : (
              <span className="bg-primary text-primary-foreground grid size-7 place-items-center rounded-md text-xs font-bold">
                {brandName.slice(0, 1).toUpperCase()}
              </span>
            )}
            <span className="hidden sm:inline">{brandName}</span>
          </>
        )}
      </Link>

      <div className="ml-auto flex items-center gap-1.5 sm:gap-2">
        {/* Search (⌘K command palette) is a staff tool — hidden for guests. */}
        {!isGuest ? (
          <>
            <button
              type="button"
              onClick={() => openSearch(true)}
              className="text-muted-foreground hover:bg-accent hidden h-9 w-56 items-center gap-2 rounded-md border px-3 text-sm sm:flex"
              aria-label="Search"
            >
              <Search className="size-4" />
              <span>Search…</span>
              <kbd className="bg-muted ml-auto rounded px-1.5 py-0.5 text-[10px] font-medium">⌘K</kbd>
            </button>
            <Button
              variant="ghost"
              size="icon"
              className="sm:hidden"
              onClick={() => openSearch(true)}
              aria-label="Search"
            >
              <Search />
            </Button>
          </>
        ) : null}
        <TimerIndicator />
        {!whiteLabel ? <OrgSwitcher /> : null}
        <NotificationBell />
        <PresenceSync />
        <ThemeToggle />
        <UserMenu />
      </div>
    </header>
  );
}
